/**
 * lib/quotePdf.ts
 * Branded A4 quotation PDF (jsPDF). Mirrors the invoice layout but
 * titled QUOTATION, with a "Valid until" date and an optional terms
 * block; no balance/payment figures.
 */
import { jsPDF } from 'jspdf';
import type { CompanySettings, Quote } from '@/types';
import { formatPula } from './money';
import { drawDocGlyph, drawWatermark, brandName } from './pdfIcons';
import { drawBankingDetails } from './pdfHeader';

const ORANGE: [number, number, number] = [255, 107, 53];
const CHAR: [number, number, number] = [34, 39, 46];
const GRAY: [number, number, number] = [120, 128, 138];
const LINE: [number, number, number] = [228, 230, 233];

function fmtDate(iso: string): string {
  return iso ? new Date(iso).toLocaleDateString('en-BW', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
}

export function quoteFileName(q: Quote): string {
  return `Quote-${q.number ?? 'draft'}.pdf`;
}

export function buildQuotePdf(q: Quote, company: CompanySettings): jsPDF {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = 210;
  const M = 15;
  const right = W - M;

  const bandY = 12;
  const bandH = 26;
  const bandW = W - 2 * M;
  doc.setFillColor(...ORANGE);
  doc.rect(M, bandY, bandW, bandH, 'F');
  const cy = bandY + bandH / 2;
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(1.4);
  doc.setLineCap('round');
  doc.setLineJoin('round');
  const cW = 3.4;
  const chX = M + 8;
  doc.line(chX, cy - 3.4, chX + cW, cy);
  doc.line(chX + cW, cy, chX, cy + 3.4);
  const wordX = chX + cW + 3;
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(brandName(company), wordX, cy - 0.5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text('PROPRIETARY LIMITED', wordX, cy + 4.5);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text('QUOTATION', M + bandW - 6, cy + 1.5, { align: 'right' });
  drawDocGlyph(doc, 'quote', M + bandW / 2, cy, 13, [255, 255, 255]);
  drawWatermark(doc, 'quote');

  let y = 48;
  doc.setTextColor(...GRAY);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10.5);
  [
    company.physicalAddress,
    company.email,
    company.phones?.join('  ·  '),
    company.registrationNumber ? `Reg: ${company.registrationNumber}` : '',
    company.tin ? `TIN: ${company.tin}` : '',
    company.website || '',
  ]
    .filter(Boolean)
    .forEach((l) => {
      doc.text(l as string, M, y);
      y += 5;
    });

  let my = 48;
  doc.setFontSize(11);
  ([
    ['Quote no.', q.number ?? 'DRAFT'],
    ['Date', fmtDate(q.issueDate)],
    ['Valid until', fmtDate(q.validUntil)],
  ] as Array<[string, string]>).forEach(([k, v]) => {
    doc.setTextColor(...GRAY);
    doc.setFont('helvetica', 'normal');
    doc.text(k, right - 62, my);
    doc.setTextColor(...CHAR);
    doc.setFont('helvetica', 'bold');
    doc.text(v, right, my, { align: 'right' });
    my += 5.5;
  });

  y = Math.max(y, my) + 6;
  doc.setTextColor(...GRAY);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('PREPARED FOR', M, y);
  y += 5.5;
  doc.setTextColor(...CHAR);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11.5);
  doc.text(q.client.name, M, y);
  y += 5.5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10.5);
  doc.setTextColor(...GRAY);
  [q.client.billingAddress, q.client.email, q.client.tin ? `TIN: ${q.client.tin}` : '']
    .filter(Boolean)
    .forEach((l) => {
      doc.text(l as string, M, y);
      y += 5;
    });

  y += 6;
  const colDesc = M;
  const xQty = 116;
  const xUnit = 152;
  const xAmt = right - 2;
  const descWrap = xQty - colDesc - 8;
  const registered = q.vat.registered;

  doc.setFillColor(255, 241, 234);
  doc.rect(M, y - 5, right - M, 9, 'F');
  doc.setTextColor(...CHAR);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.text('DESCRIPTION', colDesc + 2, y);
  doc.text('QTY', xQty, y, { align: 'right' });
  doc.text('UNIT PRICE', xUnit, y, { align: 'right' });
  doc.text('AMOUNT', xAmt, y, { align: 'right' });
  y += 7;

  doc.setFont('helvetica', 'normal');
  q.lineItems.forEach((l) => {
    const descLines = doc.splitTextToSize(l.description, descWrap) as string[];
    doc.text(descLines, colDesc + 2, y);
    doc.text(String(l.quantity), xQty, y, { align: 'right' });
    doc.text(formatPula(l.unitPrice), xUnit, y, { align: 'right' });
    doc.text(formatPula(l.lineTotal), xAmt, y, { align: 'right' });
    y += 5.5 * Math.max(1, descLines.length) + 1.5;
    doc.setDrawColor(...LINE);
    doc.line(M, y - 3, right, y - 3);
  });

  y += 4;
  const labelX = xUnit;
  const row = (label: string, value: string, bold = false) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(bold ? 13 : 11);
    doc.setTextColor(...(bold ? CHAR : GRAY));
    doc.text(label, labelX, y, { align: 'right' });
    doc.setTextColor(...CHAR);
    doc.text(value, xAmt, y, { align: 'right' });
    y += bold ? 8 : 6;
  };
  row('Subtotal', formatPula(q.totals.subtotal));
  if (registered) row(`VAT (${q.vat.rate}%)`, formatPula(q.totals.vatTotal));
  doc.setDrawColor(...CHAR);
  doc.line(labelX - 40, y - 1, right, y - 1);
  y += 5;
  row('Total', formatPula(q.totals.total), true);

  // Terms / notes
  const blocks: Array<[string, string]> = [];
  if (q.notes) blocks.push(['Notes', q.notes]);
  const terms = q.terms || company.quoteFooter;
  if (terms) blocks.push(['Terms', terms]);
  let by = Math.max(y + 8, 235);
  blocks.forEach(([label, text]) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...GRAY);
    doc.text(label.toUpperCase(), M, by);
    by += 5.5;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...CHAR);
    const tl = doc.splitTextToSize(text, right - M) as string[];
    doc.text(tl, M, by);
    by += tl.length * 4.8 + 4;
  });

  if (company.bank) {
    drawBankingDetails(doc, company.bank, M, Math.max(by + 4, 248));
  }

  if (!registered) {
    doc.setFontSize(9.5);
    doc.setTextColor(...GRAY);
    doc.text('Not registered for VAT.', right, 290, { align: 'right' });
  }

  return doc;
}

export function downloadQuotePdf(q: Quote, company: CompanySettings): void {
  buildQuotePdf(q, company).save(quoteFileName(q));
}
export function quotePdfBase64(q: Quote, company: CompanySettings): string {
  const uri = buildQuotePdf(q, company).output('datauristring');
  return uri.slice(uri.indexOf(',') + 1);
}
